import { useEffect } from 'react'
import { clone, remove, unset } from 'lodash'
import { AiOutlineClose, AiOutlinePlus } from 'react-icons/ai'
import { FiCopy } from 'react-icons/fi'
import * as Yup from 'yup'

import { Button, Checkbox, Select, SelectCreatable, TextInput } from 'Components/Form'
import { Disclosures } from 'Components/Ui'
import { checkLabel, formulaExecution } from 'Utils/helper'

import InputParameters from './InputParameters'

export const dataDefault = {
    test_type_id: '',
    position: '',
    reference_standard: '',
    user_id: [],
    inspector_id: [],
    authorised_id: [],
    details: {},
}

export const transTestSchema = Yup.object().shape({
    test_type_id: Yup.string().required('Please select test type.'),
    position: Yup.string().required('The position field is required.'),
    reference_standard: Yup.string().nullable(),
    user_id: Yup.array(),
    inspector_id: Yup.array(),
    authorised_id: Yup.array(),
    details: Yup.lazy((value, { context, parent }) => {
        var shape = {}
        var values = { ...context?.transformer, ...context?.transformer?.details, ...value }
        ;(value, parent?.test_type?.details?.input ?? [])
            .filter(e => e.required === '1' && formulaExecution(e?.condition, values))
            .map(input => {
                var required = `The ${input.name} field is required.`
                if (['0', '1', '2'].includes(input?.data_type?.toString())) {
                    shape[input.id] = Yup.string().required(required)
                } else if (input?.data_type?.toString() === '3') {
                    shape[input.id] = Yup.array().min(1, required).required(required)
                }
                return input
            })
        return Yup.object().shape(shape)
    }),
})

export default function TransTests({
    index,
    datas,
    data,
    setFieldValue,
    touched,
    errors,
    testTypes,
    users,
    inspectors,
    valueJSON,
    transformer,
    inspectorType = true,
}) {
    const name = `trans_test[${index}].`
    const testType = testTypes.find(e => e.id === data.test_type_id)
    useEffect(() => {
        if (!inspectorType) {
            setFieldValue(`${name}inspector_id`, [])
        }
        // eslint-disable-next-line
    }, [inspectorType])
    return (
        <Disclosures
            title={index + 1 + ' - ' + (testType?.name ?? '')}
            error={touched && errors}
            subtitle={
                <div className="flex items-center gap-2">
                    {index + 1 === datas.length ? (
                        <Button
                            type="button"
                            className="text-white border-primary bg-primary dark:border-secondary dark:bg-secondary"
                            onClick={e => {
                                e.stopPropagation()
                                const saveTask = clone(datas)
                                const defaults = clone(dataDefault)
                                defaults.user_id = data.user_id
                                defaults.inspector_id = data.inspector_id
                                defaults.authorised_id = data.authorised_id
                                saveTask.push(defaults)
                                setFieldValue('trans_test', saveTask)
                            }}
                        >
                            <AiOutlinePlus size={20} />
                        </Button>
                    ) : null}
                    <Button
                        type="button"
                        className="text-white border-warning bg-warning"
                        onClick={e => {
                            e.stopPropagation()
                            const saveTask = clone(datas)
                            const newData = saveTask.find((n, key) => key === index)
                            unset(newData, 'id')
                            saveTask.push(newData)
                            setFieldValue('trans_test', saveTask)
                        }}
                    >
                        <FiCopy size={20} />
                    </Button>
                    {datas.length > 1 ? (
                        <Button
                            type="button"
                            className="text-white border-danger bg-danger"
                            onClick={e => {
                                e.stopPropagation()
                                const saveTask = clone(datas)
                                remove(saveTask, (n, key) => key === index)
                                setFieldValue('trans_test', saveTask)
                            }}
                        >
                            <AiOutlineClose size={20} />
                        </Button>
                    ) : null}
                </div>
            }
            defaultOpen={index === 0}
        >
            <div className="flex flex-col gap-3">
                <div className={`grid grid-cols-2 gap-3 ${inspectorType ? 'sm:grid-cols-11' : 'sm:grid-cols-9'}`}>
                    <Select
                        id={`${name}test_type_id`}
                        name={`${name}test_type_id`}
                        className="col-span-2 sm:col-span-3"
                        label="Test"
                        placeholder="Test"
                        value={data.test_type_id}
                        options={testTypes.map(e => ({
                            id: e.id,
                            label: checkLabel(`${e.name} (P${e.position}-V${e.version_number})`, valueJSON),
                        }))}
                        onChange={e => {
                            setFieldValue(`${name}test_type_id`, e.target.value)
                            var testType = testTypes.find(t => t.id === e.target.value)
                            setFieldValue(`${name}test_type`, testType)
                            setFieldValue(`${name}position`, testType?.position ?? 0)
                            var referenceStandard = (testType?.reference_standards ?? []).find(
                                r => r.reference_standard_id === (transformer?.reference_standard_id ?? '')
                            )
                            setFieldValue(`${name}reference_standard`, referenceStandard?.name ?? '')
                            setFieldValue(
                                `${name}details`,
                                Object.fromEntries(
                                    (testType.details.input ?? [])
                                        .filter(e => e.data_type.toString() === '2')
                                        .map(e => [e.id, ''])
                                )
                            )
                        }}
                        error={touched?.test_type_id && errors?.test_type_id}
                    />
                    <TextInput
                        type="number"
                        id={`${name}position`}
                        name={`${name}position`}
                        label="Position"
                        className="sm:col-span-2"
                        error={touched?.position && errors?.position}
                    />
                    <TextInput
                        id={`${name}reference_standard`}
                        name={`${name}reference_standard`}
                        label="Reference standard"
                        className="sm:col-span-2"
                        error={touched?.reference_standard && errors?.reference_standard}
                    />
                    <SelectCreatable
                        id={`${name}user_id`}
                        name={`${name}user_id`}
                        className="sm:col-span-2"
                        label="Users"
                        placeholder="Users"
                        value={(users ?? [])
                            .filter(e => (data?.user_id ?? []).includes(e.id))
                            .map(e => ({
                                value: e.id,
                                label: e.name,
                            }))}
                        options={(users ?? []).map(e => ({
                            value: e.id,
                            label: e.name,
                        }))}
                        onChange={e =>
                            setFieldValue(
                                `${name}user_id`,
                                e.map(f => f.value)
                            )
                        }
                        isMulti
                    />
                    {inspectorType && (
                        <SelectCreatable
                            id={`${name}inspector_id`}
                            name={`${name}inspector_id`}
                            className="sm:col-span-2"
                            label="Inspectors"
                            placeholder="Inspectors"
                            value={(inspectors ?? [])
                                .filter(e => (data?.inspector_id ?? []).includes(e.id))
                                .map(e => ({
                                    value: e.id,
                                    label: e.name,
                                }))}
                            options={(inspectors ?? []).map(e => ({
                                value: e.id,
                                label: e.name,
                            }))}
                            onChange={e =>
                                setFieldValue(
                                    `${name}inspector_id`,
                                    e.map(f => f.value)
                                )
                            }
                            isMulti
                        />
                    )}
                </div>
                {(data.inspector_id ?? []).length > 0 && (
                    <Checkbox
                        group={true}
                        name={`${name}authorised_id`}
                        label="Inspectors Authorised"
                        value={data.authorised_id ?? []}
                        className="grid grid-cols-4 gap-3"
                        datas={inspectors
                            .filter(e => (data.inspector_id ?? []).includes(e.id))
                            .map(e => ({ id: name + e.id, label: e.name, value: e.id }))}
                        error={touched?.authorised_id && errors?.authorised_id}
                    />
                )}
                <div className={`grid grid-cols-2 gap-3 sm:grid-cols-10`}>
                    {(data?.test_type_id ? testTypes?.find(({ id }) => id === data.test_type_id)?.details?.input : [])?.map(
                        (input, index) =>
                            formulaExecution(input?.condition, { ...valueJSON, ...data?.details }) ? (
                                <InputParameters
                                    key={index}
                                    data={input}
                                    datas={data}
                                    valueJSON={valueJSON}
                                    name={`${name}details.`}
                                    setFieldValue={setFieldValue}
                                    errors={errors?.details ?? null}
                                    className="sm:col-span-3"
                                />
                            ) : null
                    )}
                </div>
            </div>
        </Disclosures>
    )
}
